import React from 'react';
import { StatusChip } from '../ui/StatusChip';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
interface PaymentPlan {
  id: string;
  clienteId: string;
  clienteNome: string;
  clientePiva: string;
  nomePiano: string;
  stato: 'draft' | 'pending' | 'active' | 'paused' | 'canceled' | 'completed';
  importoTotale: number;
  importoSaldato: number;
  importoRimanente: number;
  ratePagate: number;
  rateDaPagare: number;
  rateSaltate: number;
  dataCreazione: string;
  dataScadenza: string;
}
interface PlanViewProps {
  searchTerm: string;
  dateRange: {
    start: Date | null;
    end: Date | null;
    preset?: string;
  };
  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSort: (field: string) => void;
}
export function PlanView({
  searchTerm,
  dateRange,
  sortField,
  sortDirection,
  onSort
}: PlanViewProps) {
  // Dati fittizi per la tabella
  const piani: PaymentPlan[] = [{
    id: 'plan-1',
    clienteId: '1',
    clienteNome: 'Acme Srl',
    clientePiva: '12345678901',
    nomePiano: 'Piano rientro Q1 2023',
    stato: 'active',
    importoTotale: 12500,
    importoSaldato: 5000,
    importoRimanente: 7500,
    ratePagate: 2,
    rateDaPagare: 3,
    rateSaltate: 0,
    dataCreazione: '2023-04-15',
    dataScadenza: '2023-09-15'
  }, {
    id: 'plan-2',
    clienteId: '1',
    clienteNome: 'Acme Srl',
    clientePiva: '12345678901',
    nomePiano: 'Piano rientro Q2 2023',
    stato: 'draft',
    importoTotale: 4800,
    importoSaldato: 0,
    importoRimanente: 4800,
    ratePagate: 0,
    rateDaPagare: 6,
    rateSaltate: 0,
    dataCreazione: '2023-05-20',
    dataScadenza: '2023-11-20'
  }, {
    id: 'plan-3',
    clienteId: '2',
    clienteNome: 'TechSolutions Spa',
    clientePiva: '98765432101',
    nomePiano: 'Piano rientro Annuale 2023',
    stato: 'pending',
    importoTotale: 35000,
    importoSaldato: 0,
    importoRimanente: 35000,
    ratePagate: 0,
    rateDaPagare: 10,
    rateSaltate: 0,
    dataCreazione: '2023-05-10',
    dataScadenza: '2024-03-10'
  }, {
    id: 'plan-4',
    clienteId: '3',
    clienteNome: 'Industria Meccanica Srl',
    clientePiva: '45678912301',
    nomePiano: 'Piano rientro Marzo-Agosto',
    stato: 'active',
    importoTotale: 18000,
    importoSaldato: 6000,
    importoRimanente: 12000,
    ratePagate: 2,
    rateDaPagare: 4,
    rateSaltate: 1,
    dataCreazione: '2023-03-05',
    dataScadenza: '2023-09-05'
  }, {
    id: 'plan-5',
    clienteId: '4',
    clienteNome: 'Logistica Express Srl',
    clientePiva: '78901234561',
    nomePiano: 'Piano rientro Semestrale',
    stato: 'active',
    importoTotale: 24000,
    importoSaldato: 8000,
    importoRimanente: 16000,
    ratePagate: 2,
    rateDaPagare: 4,
    rateSaltate: 0,
    dataCreazione: '2023-02-15',
    dataScadenza: '2023-08-15'
  }, {
    id: 'plan-6',
    clienteId: '4',
    clienteNome: 'Logistica Express Srl',
    clientePiva: '78901234561',
    nomePiano: 'Piano rientro Precedente',
    stato: 'completed',
    importoTotale: 9600,
    importoSaldato: 9600,
    importoRimanente: 0,
    ratePagate: 6,
    rateDaPagare: 0,
    rateSaltate: 0,
    dataCreazione: '2022-08-10',
    dataScadenza: '2023-02-10'
  }, {
    id: 'plan-7',
    clienteId: '5',
    clienteNome: 'Alimentari Freschi Spa',
    clientePiva: '56789012341',
    nomePiano: 'Piano rientro Urgente',
    stato: 'canceled',
    importoTotale: 7500,
    importoSaldato: 2500,
    importoRimanente: 5000,
    ratePagate: 1,
    rateDaPagare: 0,
    rateSaltate: 2,
    dataCreazione: '2023-01-20',
    dataScadenza: '2023-04-20'
  }];
  // Filtraggio per termine di ricerca
  const filteredPiani = piani.filter(piano => {
    const matchesSearch = piano.clienteNome.toLowerCase().includes(searchTerm.toLowerCase()) || piano.clientePiva.includes(searchTerm) || piano.stato.includes(searchTerm.toLowerCase()) || piano.importoTotale.toString().includes(searchTerm) || piano.importoSaldato.toString().includes(searchTerm) || piano.importoRimanente.toString().includes(searchTerm);
    // Filtraggio per data
    const matchesDateRange = !dateRange.start || !dateRange.end || (() => {
      const pianoDate = new Date(piano.dataCreazione);
      return pianoDate >= dateRange.start! && pianoDate <= dateRange.end!;
    })();
    return matchesSearch && matchesDateRange;
  });
  // Ordinamento dei piani
  const sortedPiani = [...filteredPiani].sort((a, b) => {
    if (sortField === 'clienteNome') {
      return sortDirection === 'asc' ? a.clienteNome.localeCompare(b.clienteNome) : b.clienteNome.localeCompare(a.clienteNome);
    }
    if (sortField === 'stato') {
      return sortDirection === 'asc' ? a.stato.localeCompare(b.stato) : b.stato.localeCompare(a.stato);
    }
    if (sortField === 'importoTotale' || sortField === 'importoSaldato' || sortField === 'importoRimanente') {
      return sortDirection === 'asc' ? (a[sortField as keyof PaymentPlan] as number) - (b[sortField as keyof PaymentPlan] as number) : (b[sortField as keyof PaymentPlan] as number) - (a[sortField as keyof PaymentPlan] as number);
    }
    if (sortField === 'ratePagate' || sortField === 'rateDaPagare' || sortField === 'rateSaltate') {
      return sortDirection === 'asc' ? (a[sortField as keyof PaymentPlan] as number) - (b[sortField as keyof PaymentPlan] as number) : (b[sortField as keyof PaymentPlan] as number) - (a[sortField as keyof PaymentPlan] as number);
    }
    if (sortField === 'dataCreazione') {
      return sortDirection === 'asc' ? new Date(a.dataCreazione).getTime() - new Date(b.dataCreazione).getTime() : new Date(b.dataCreazione).getTime() - new Date(a.dataCreazione).getTime();
    }
    return 0;
  });
  // Formattazione dell'importo in euro
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };
  // Mapping dello stato per il componente StatusChip
  const mapStatoToStatusType = (stato: string): any => {
    const mapping: Record<string, any> = {
      draft: 'draft',
      pending: 'pending',
      active: 'active',
      paused: 'paused',
      canceled: 'canceled',
      completed: 'paid'
    };
    return mapping[stato] || 'draft';
  };
  // Etichette per gli stati
  const statoLabels: Record<string, string> = {
    draft: 'Bozza',
    pending: 'In Firma',
    active: 'Firmato',
    paused: 'In Pausa',
    canceled: 'Annullato',
    completed: 'Completato'
  };
  const renderSortIcon = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-1006" /> : <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-1007" />;
  };
  return <div className="overflow-x-auto" data-id="element-1008">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-1009">
        <thead className="bg-gray-50" data-id="element-1010">
          <tr data-id="element-1011">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => onSort('clienteNome')} data-id="element-1012">
              <div className="flex items-center" data-id="element-1013">
                Cliente
                {renderSortIcon('clienteNome')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => onSort('stato')} data-id="element-1014">
              <div className="flex items-center" data-id="element-1015">
                Stato
                {renderSortIcon('stato')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => onSort('importoTotale')} data-id="element-1016">
              <div className="flex items-center" data-id="element-1017">
                Importo Totale
                {renderSortIcon('importoTotale')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => onSort('importoSaldato')} data-id="element-1018">
              <div className="flex items-center" data-id="element-1019">
                Importo Saldato
                {renderSortIcon('importoSaldato')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => onSort('importoRimanente')} data-id="element-1020">
              <div className="flex items-center" data-id="element-1021">
                Importo Rimanente
                {renderSortIcon('importoRimanente')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => onSort('ratePagate')} data-id="element-1022">
              <div className="flex items-center" data-id="element-1023">
                Rate Pagate
                {renderSortIcon('ratePagate')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => onSort('rateDaPagare')} data-id="element-1024">
              <div className="flex items-center" data-id="element-1025">
                Rate da Pagare
                {renderSortIcon('rateDaPagare')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => onSort('rateSaltate')} data-id="element-1026">
              <div className="flex items-center" data-id="element-1027">
                Rate Saltate
                {renderSortIcon('rateSaltate')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => onSort('dataCreazione')} data-id="element-1028">
              <div className="flex items-center" data-id="element-1029">
                Data Creazione
                {renderSortIcon('dataCreazione')}
              </div>
            </th>
            <th scope="col" className="relative px-6 py-3" data-id="element-1030">
              <span className="sr-only" data-id="element-1031">Azioni</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1032">
          {sortedPiani.length === 0 ? <tr data-id="element-1033">
              <td colSpan={10} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-1034">
                Nessun piano di rientro trovato
              </td>
            </tr> : sortedPiani.map(piano => <tr key={piano.id} className="hover:bg-gray-50" data-id="element-1035">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1036">
                  <div className="text-sm font-medium text-gray-900" data-id="element-1037">
                    {piano.clienteNome}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-1038">
                    {piano.clientePiva}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1039">
                  <StatusChip status={mapStatoToStatusType(piano.stato)} label={statoLabels[piano.stato]} data-id="element-1040" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1041">
                  <div className="text-sm font-medium text-gray-900" data-id="element-1042">
                    {formatCurrency(piano.importoTotale)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1043">
                  <div className="text-sm text-green-600 font-medium" data-id="element-1044">
                    {formatCurrency(piano.importoSaldato)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1045">
                  <div className="text-sm text-red-600 font-medium" data-id="element-1046">
                    {formatCurrency(piano.importoRimanente)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1047">
                  <div className="text-sm text-gray-900" data-id="element-1048">
                    {piano.ratePagate}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1049">
                  <div className="text-sm text-gray-900" data-id="element-1050">
                    {piano.rateDaPagare}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1051">
                  <div className={`text-sm font-medium ${piano.rateSaltate > 0 ? 'text-red-600' : 'text-gray-900'}`} data-id="element-1052">
                    {piano.rateSaltate}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1053">
                  <div className="text-sm text-gray-500" data-id="element-1054">
                    {new Date(piano.dataCreazione).toLocaleDateString('it-IT')}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1055">
                  <a href="#" className="text-blue-600 hover:text-blue-900" data-id="element-1056">
                    Dettagli
                  </a>
                </td>
              </tr>)}
        </tbody>
      </table>
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-1057">
        <div className="flex-1 flex justify-between sm:hidden" data-id="element-1058">
          <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1059">
            Precedente
          </button>
          <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1060">
            Successivo
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-1061">
          <div data-id="element-1062">
            <p className="text-sm text-gray-700" data-id="element-1063">
              Visualizzazione{' '}
              <span className="font-medium" data-id="element-1064">{sortedPiani.length}</span> di{' '}
              <span className="font-medium" data-id="element-1065">{piani.length}</span> piani
            </p>
          </div>
          <div data-id="element-1066">
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-1067">
              <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-1068">
                <span className="sr-only" data-id="element-1069">Precedente</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-1070">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-1071" />
                </svg>
              </button>
              <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1072">
                1
              </button>
              <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-1073">
                <span className="sr-only" data-id="element-1074">Successivo</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-1075">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" data-id="element-1076" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>;
}